import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { ICONS } from "@/pages/Home";
import { useLang } from "@/i18n/LanguageContext";
import { IMAGES, SERVICES, SERVICE_ACCENTS, pick } from "@/data/site";

export default function Services() {
  const { t, lang } = useLang();
  return (
    <div data-testid="services-page">
      <Seo
        title="Services — V.B. Sharada Associates | Contracting, Planning, Construction, Interiors, Tandur"
        description="General contracting, building planning and approvals, building construction, interior execution, and structural engineering services in Tandur, Telangana."
      />

      <section className="warm-grid border-b border-orange-100">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            {t("svc.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 max-w-3xl">
            {t("svc.h1")}
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl">{t("svc.para")}</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto space-y-16">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const accent = SERVICE_ACCENTS[i % SERVICE_ACCENTS.length];
            const flip = i % 2 === 1;
            return (
              <article
                key={s.id}
                data-testid={`service-section-${s.id}`}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center"
              >
                <div className={flip ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-2xl ${accent.chip} flex items-center justify-center shadow-sm`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`font-heading text-sm font-extrabold px-3 py-1 rounded-full ${accent.chip}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{pick(s, "title", lang)}</h2>
                  <p className="mt-3 text-base text-slate-600 leading-relaxed">{pick(s, "short", lang)}</p>
                  <ul className="mt-6 space-y-3">
                    {pick(s, "points", lang).map((pt) => (
                      <li key={pt} className="flex items-start gap-3 text-sm text-slate-700">
                        <CheckCircle2 className={`w-4 h-4 ${accent.check} mt-0.5 shrink-0`} />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-enquire-${s.id}`}
                    className="mt-7 inline-flex items-center gap-2 text-orange-600 font-semibold text-sm hover:gap-3 transition-all duration-200"
                  >
                    {t("svc.enquire")} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className={`relative ${flip ? "lg:order-1" : ""}`}>
                  <div className={`absolute w-full h-full rounded-3xl bg-gradient-to-br ${accent.bar} opacity-20 ${flip ? "-top-4 -right-4" : "-top-4 -left-4"}`} />
                  <img
                    src={IMAGES[s.image]}
                    alt={`${s.title} by V.B. Sharada Associates`}
                    className="relative rounded-3xl shadow-lg w-full h-72 sm:h-80 object-cover"
                  />
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="py-14 bg-gradient-to-r from-orange-600 via-amber-600 to-teal-600">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">{t("svc.ctaH2")}</h2>
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="inline-flex items-center gap-2 bg-white text-orange-600 font-semibold px-7 py-3.5 rounded-full hover:bg-orange-50 transition-colors duration-200 shrink-0 shadow-lg"
          >
            {t("svc.ctaBtn")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
